import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { AdminLayout } from '@/layouts/admin-layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'

interface Feedback {
  id: number
  category: string
  title: string
  description: string
  context: Record<string, any>
  status: string
  priority: string
  occurrences_count: number
  dev_instance_id: number | null
  dev_instance_name: string | null
  conversation_id: number | null
  resolution_notes: string | null
  resolved_by: string | null
  resolved_at: string | null
  created_at: string
}

interface Props {
  feedback: Feedback
  valid_statuses: string[]
}

const STATUS_COLORS: Record<string, string> = {
  new: 'default',
  acknowledged: 'secondary',
  planned: 'warning',
  resolved: 'success',
  wont_fix: 'destructive',
}

const PRIORITY_COLORS: Record<string, string> = {
  low: 'secondary',
  medium: 'default',
  high: 'destructive',
}

function Show({ feedback }: Props) {
  const [resolutionNotes, setResolutionNotes] = useState('')
  const basePath = '/admin/platform_feedbacks'

  const handleAcknowledge = () => {
    router.post(`${basePath}/${feedback.id}/acknowledge`)
  }

  const handleResolve = () => {
    router.post(`${basePath}/${feedback.id}/resolve`, { resolution_notes: resolutionNotes })
  }

  const handleWontFix = () => {
    router.post(`${basePath}/${feedback.id}/wont_fix`, { resolution_notes: resolutionNotes })
  }

  const isOpen = ['new', 'acknowledged', 'planned'].includes(feedback.status)

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 py-4">
          <div className="px-4 lg:px-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Link href={basePath} className="text-sm text-muted-foreground hover:text-foreground">
                  ← Feedback
                </Link>
                <h1 className="text-lg font-semibold">{feedback.title}</h1>
                <Badge variant={STATUS_COLORS[feedback.status] as any}>
                  {feedback.status.replace('_', ' ')}
                </Badge>
                <Badge variant={PRIORITY_COLORS[feedback.priority] as any}>
                  {feedback.priority}
                </Badge>
              </div>
              {feedback.status === 'new' && (
                <Button variant="secondary" size="sm" onClick={handleAcknowledge}>
                  Acknowledge
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 flex flex-col gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Description</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-sm whitespace-pre-wrap font-mono bg-muted p-3 rounded-md">{feedback.description}</pre>
                  </CardContent>
                </Card>

                {feedback.context?.previous_submissions?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">
                        Previous Submissions ({feedback.context.previous_submissions.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {feedback.context.previous_submissions.map((sub: any, i: number) => (
                        <div key={i} className="border border-border rounded-md p-3 space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{sub.title || 'No title'}</span>
                            <span className="text-xs text-muted-foreground">
                              {sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </div>
                          {sub.description && (
                            <p className="text-xs text-muted-foreground">{sub.description}</p>
                          )}
                          <div className="flex gap-3 text-[10px] text-muted-foreground">
                            {sub.dev_instance_id && <span>Instance: {sub.dev_instance_id}</span>}
                            {sub.conversation_id && <span>Conversation: {sub.conversation_id}</span>}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {feedback.context && Object.keys(feedback.context).filter(k => k !== 'previous_submissions').length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Context</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <pre className="text-xs whitespace-pre-wrap font-mono bg-muted p-3 rounded-md overflow-auto max-h-64">
                        {JSON.stringify(Object.fromEntries(Object.entries(feedback.context).filter(([k]) => k !== 'previous_submissions')), null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                )}

                {feedback.resolution_notes && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Resolution</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      <pre className="text-sm whitespace-pre-wrap font-mono bg-muted p-3 rounded-md">{feedback.resolution_notes}</pre>
                      <p className="text-xs text-muted-foreground">
                        {feedback.resolved_by && `By ${feedback.resolved_by}`}
                        {feedback.resolved_at && ` on ${new Date(feedback.resolved_at).toLocaleDateString()}`}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {isOpen && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Resolve</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Textarea
                        placeholder="Resolution notes..."
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        className="text-sm"
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleResolve} disabled={!resolutionNotes.trim()}>
                          Resolve
                        </Button>
                        <Button size="sm" variant="destructive" onClick={handleWontFix} disabled={!resolutionNotes.trim()}>
                          Won't Fix
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              <div className="flex flex-col gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Category</span>
                      <Badge variant="secondary">{feedback.category.replace('_', ' ')}</Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Priority</span>
                      <Badge variant={PRIORITY_COLORS[feedback.priority] as any}>{feedback.priority}</Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Occurrences</span>
                      <span>{feedback.occurrences_count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Created</span>
                      <span>{new Date(feedback.created_at).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Source</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1 text-xs text-muted-foreground">
                    {feedback.dev_instance_id && <p>Instance: {feedback.dev_instance_name || feedback.dev_instance_id}</p>}
                    {feedback.conversation_id && <p>Conversation: {feedback.conversation_id}</p>}
                    {!feedback.dev_instance_id && !feedback.conversation_id && <p>No source tracking</p>}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
